import React from "react";
import { Typography } from "antd";
import { Trans } from "react-i18next";
import { useSelector } from "react-redux";

import { selectCurrentApplication } from "../../../../../store/selector";

const { Paragraph } = Typography;

const transComponents = Object.freeze({ bold: <strong />, br: <br /> });

/**
 * Native JSON field type `consentText`: a first-class concept for the legal/consent copy the EE forms
 * render as text (not an input). It renders one or more i18n segments as a SINGLE paragraph — the way
 * the hand-written forms did — and each segment may be gated on the associated drug, e.g.
 * pfizer-oral's "18 or older" attestation, which shows for every drug except bosulif / xalkori.
 *
 * The segments deliberately share one <Paragraph> rather than getting a paragraph (and a form row)
 * each: the copy is written to read as continuous text (the keys carry their own <br> breaks), so a
 * paragraph per segment adds a blank line between them, and a gated-out segment would leave an empty
 * row behind. It reads the current application's drug name INTERNALLY and registers NOTHING
 * (display-only → bundle unchanged).
 *
 * FormJson usage:
 *   { "type": "consentText", "name": "", "colSize": 24,
 *     "dedicatedFieldProps": { "segments": [
 *       { "i18nKey": "<always-shown key>" },
 *       { "i18nKey": "<gated key>", "excludeDrugs": ["bosulif", "xalkori"] }
 *     ] } }
 */
export const ConsentTextField = React.memo(({ segments = [] }) => {
  const currentApplication = useSelector(selectCurrentApplication);
  const drug = currentApplication?.drugDisplayName?.toLowerCase();

  const isExcludedForDrug = (excludeDrugs) =>
    Array.isArray(excludeDrugs) && !!drug && excludeDrugs.some((excluded) => String(excluded).toLowerCase() === drug);

  const visibleSegments = segments.filter(({ i18nKey, excludeDrugs }) => i18nKey && !isExcludedForDrug(excludeDrugs));

  if (!visibleSegments.length) {
    return null;
  }

  return (
    <Paragraph>
      {visibleSegments.map(({ i18nKey }) => (
        <Trans key={i18nKey} i18nKey={i18nKey} components={transComponents} />
      ))}
    </Paragraph>
  );
});

ConsentTextField.displayName = "ConsentTextField";

export default ConsentTextField;
